import { useMemo } from 'react';
import type { GroupBase } from 'react-select';
import type { EstimationScheme } from '@/lib/api/types/estimationScheme';
import { useGetEstimationSchemesQuery } from '@/lib/queries/common/useGetEstimationScheme';
import { formatFriendlyRange } from '@/lib/utils/estimates/friendlyDurations';

export const SETTINGS_GROUP_LABEL = '__settings__';
export const SETTINGS_OPTION_VALUE = -1;
export const ESTIMATION_MENU_WIDTH = 246;

export type EstimationOption = {
  value: number;
  label: string;
  effortRange: string | null;
  schemeId: number;
};

export type EstimationGroup = GroupBase<EstimationOption>;

export const formatEffortRange = (
  lowEffort: number | null,
  highEffort: number | null,
): string | null => {
  if (lowEffort == null || highEffort == null) {
    return null;
  }
  return `(${formatFriendlyRange(lowEffort, highEffort)})`;
};

export const mapSchemeToGroup = (scheme: EstimationScheme): EstimationGroup => ({
  label: scheme.name,
  options: scheme.estimations.map((estimation) => ({
    value: estimation.id,
    label: estimation.label,
    effortRange: formatEffortRange(estimation.lowEffort, estimation.highEffort),
    schemeId: scheme.id,
  })),
});

export const isSettingsOption = (option: EstimationOption) =>
  option.value === SETTINGS_OPTION_VALUE;

export const filterEstimationOption = (
  option: { data: EstimationOption; label: string },
  inputValue: string,
): boolean =>
  isSettingsOption(option.data) ||
  !inputValue ||
  option.label.toLowerCase().includes(inputValue.toLowerCase());

export const formatGroupLabel = (group: EstimationGroup) => {
  if (group.label === SETTINGS_GROUP_LABEL) {
    return null;
  }
  return group.label;
};

type EstimationWithScheme = {
  id: number;
  label: string;
  lowEffort: number | null;
  highEffort: number | null;
  estimationSchemeId: number;
};

export const findSelectedGroupedOption = ({
  estimationId,
  flatOptions,
  estimations,
}: {
  estimationId: number | null | undefined;
  flatOptions: EstimationOption[];
  estimations: EstimationWithScheme[];
}): EstimationOption | null => {
  const fromFlat = flatOptions.find((opt) => opt.value === estimationId);
  if (fromFlat) {
    return fromFlat;
  }
  const est = estimations.find((e) => e.id === estimationId);
  return est
    ? {
        value: est.id,
        label: est.label,
        effortRange: formatEffortRange(est.lowEffort, est.highEffort),
        schemeId: est.estimationSchemeId,
      }
    : null;
};

export const useGroupedEstimationOptions = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data: schemes, isLoading } = useGetEstimationSchemesQuery({ enabled });

  const groupedOptions = useMemo<EstimationGroup[]>(() => {
    if (!schemes) {
      return [];
    }

    const groups = schemes.map(mapSchemeToGroup);

    groups.push({
      label: SETTINGS_GROUP_LABEL,
      options: [
        {
          value: SETTINGS_OPTION_VALUE,
          label: SETTINGS_GROUP_LABEL,
          effortRange: null,
          schemeId: -1,
        },
      ],
    });

    return groups;
  }, [schemes]);

  const flatOptions = useMemo<EstimationOption[]>(
    () => groupedOptions.flatMap((g) => g.options).filter((o) => o.value !== SETTINGS_OPTION_VALUE),
    [groupedOptions],
  );

  return { groupedOptions, flatOptions, isLoading };
};
